import React, { useCallback, useEffect, useRef, useState } from "react";
import { Space, Typography, theme } from "antd";
import {
  ArrowLeftOutlined,
  CloudOutlined,
  CloudServerOutlined,
  CloudUploadOutlined,
  MailOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import EmailSmtpPanel from "./components/EmailSmtpPanel";
import { EMAIL_PLUGIN_ENDPOINT, FTP_ENDPOINT, STORAGE_ENDPOINT } from "./constants";

const { Text } = Typography;

/** One row in the plugin list — icon, name, what it does, and where it stands. */
const PluginCard = ({ icon, title, description, status, onClick }) => {
  const { token } = theme.useToken();

  return (
    <Card hoverable padding="sm" onClick={onClick} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: token.borderRadiusLG,
            background: token.colorPrimaryBg,
            color: token.colorPrimary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 15 }}>
            {title}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {description}
            </Text>
          </div>
        </div>

        <Space size={12}>
          {status !== null && (
            <Badge variant={status ? "success" : "neutral"}>
              {status ? "Configured" : "Not configured"}
            </Badge>
          )}
          <RightOutlined style={{ color: token.colorTextTertiary, fontSize: 12 }} />
        </Space>
      </div>
    </Card>
  );
};

/**
 * System integrations, each on its own card. Email (SMTP) is configured right here;
 * the FTP, S3 and backup cards land on the Backup & Restore screen that already owns them.
 */
const PluginsPage = () => {
  const navigate = useNavigate();
  const { mutate } = useMutations();
  // Stable handle for the load effect — same pattern as BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  // null = list; "email" = the SMTP configuration.
  const [active, setActive] = useState(null);

  const [emailConfigured, setEmailConfigured] = useState(null);
  const [ftpConfigured, setFtpConfigured] = useState(null);
  const [storageConfigured, setStorageConfigured] = useState(null);

  const loadStatuses = useCallback(async () => {
    await Promise.all([
      mutateRef.current("pluginEmailStatus", {
        method: "get",
        endpoint: EMAIL_PLUGIN_ENDPOINT,
        onSuccess: (data) => setEmailConfigured(!!data?.isConfigured),
      }),
      mutateRef.current("pluginFtpStatus", {
        method: "get",
        endpoint: FTP_ENDPOINT,
        onSuccess: (data) => setFtpConfigured((data || []).some((c) => c.isActive)),
      }),
      mutateRef.current("pluginStorageStatus", {
        method: "get",
        endpoint: STORAGE_ENDPOINT,
        onSuccess: (data) => setStorageConfigured(!!data?.isConfigured),
      }),
    ]);
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  if (active === "email") {
    return (
      <div>
        <Button
          variant="ghost"
          icon={<ArrowLeftOutlined />}
          onClick={() => setActive(null)}
          style={{ marginBottom: 8 }}
        >
          Plugins
        </Button>

        <PageHeader
          title="Email (SMTP)"
          subtitle="System emails (welcome mails, invoices, reminders, alerts)."
          extra={
            emailConfigured !== null && (
              <Badge variant={emailConfigured ? "success" : "neutral"}>
                {emailConfigured ? "Configured" : "Not configured"}
              </Badge>
            )
          }
        />

        <EmailSmtpPanel onChanged={loadStatuses} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Plugins" subtitle="Configure the system's integrations." />

      <Space direction="vertical" size={12} style={{ width: "100%", maxWidth: 860 }}>
        <PluginCard
          icon={<MailOutlined />}
          title="Email (SMTP)"
          description="System emails (welcome mails, invoices, reminders, alerts)."
          status={emailConfigured}
          onClick={() => setActive("email")}
        />

        <PluginCard
          icon={<CloudServerOutlined />}
          title="Backup & Restore"
          description="Full database archives and restore — including across SQL Server and PostgreSQL."
          status={null}
          onClick={() => navigate("/backups")}
        />

        <PluginCard
          icon={<CloudUploadOutlined />}
          title="FTP Connection"
          description="An FTP target for off-site copies of backup archives."
          status={ftpConfigured}
          onClick={() => navigate("/backups")}
        />

        <PluginCard
          icon={<CloudOutlined />}
          title="S3 Connection"
          description="One S3 connection (Wasabi, MinIO, AWS…) for the whole system — features use it to store files off the server."
          status={storageConfigured}
          onClick={() => navigate("/backups")}
        />
      </Space>
    </div>
  );
};

export default PluginsPage;
